import { z } from 'zod'

export const MCP_PROFILE_MAX_TOOLS = 25
export const MCP_PROFILE_SERVICE_TOOL_NAMES = [
  'get_balance', 'check_generation', 'request_upload_url', 'finalize_upload', 'upload_from_url'
] as const

const id = z.number().int().positive()
const description = z.string().max(2000)
export const McpPublicIdSchema = z.string().length(16).regex(/^[A-Za-z0-9]{16}$/)
export const isMcpProfileServiceToolName = (name: string) =>
  MCP_PROFILE_SERVICE_TOOL_NAMES.some(reserved => reserved === name)
// Read side: format only. A name that becomes reserved later must not make a stored profile
// unreadable; the server builder skips it and reports RESERVED_TOOL_NAME instead.
export const StoredMcpProfileToolNameSchema = z.string().regex(/^[a-z][a-z0-9_]{2,63}$/)
// Write side: format plus the reserved service names.
export const McpProfileToolNameSchema = StoredMcpProfileToolNameSchema
  .refine(name => name === name.trim(), 'Tool names cannot contain whitespace')
  .refine(name => !isMcpProfileServiceToolName(name), 'Tool name is reserved for a profile service tool')

export const McpProfileToolInputSchema = z.object({
  workflow_id: id,
  tool_name: McpProfileToolNameSchema,
  tool_description: description.nullable().optional()
}).strict()

// Array order is authoritative; callers cannot supply their own positions or IDs.
export const McpProfileInputSchema = z.object({
  name: z.string().trim().min(1).max(100),
  description: description.nullable().optional(),
  is_active: z.boolean(),
  tools: z.array(McpProfileToolInputSchema).min(1).max(MCP_PROFILE_MAX_TOOLS)
}).strict().superRefine((profile, ctx) => {
  const workflows = new Set<number>()
  const names = new Set<string>()
  profile.tools.forEach((tool, index) => {
    if (workflows.has(tool.workflow_id)) {
      ctx.addIssue({ code: 'custom', path: ['tools', index, 'workflow_id'], message: 'Duplicate workflow' })
    }
    if (names.has(tool.tool_name)) {
      ctx.addIssue({ code: 'custom', path: ['tools', index, 'tool_name'], message: 'Duplicate tool name' })
    }
    workflows.add(tool.workflow_id)
    names.add(tool.tool_name)
  })
})

export const McpProfileRowSchema = z.object({
  id,
  organization_id: id,
  public_id: McpPublicIdSchema,
  name: z.string().min(1).max(100).refine(value => value.trim().length > 0),
  description: description.nullable(),
  is_active: z.union([z.literal(0), z.literal(1)]).transform(value => value === 1),
  // Profiles belong to the organization and survive deletion of their creator.
  created_by: z.string().min(1).nullable(),
  created_at: z.string().min(1),
  updated_at: z.string().min(1)
}).strict()

// List rows carry a binding count computed in the same query; the child rows are not loaded.
export const McpProfileSummaryRowSchema = McpProfileRowSchema.extend({
  tool_count: z.number().int().min(0)
}).strict()

export const McpProfileToolRowSchema = z.object({
  id,
  server_id: id,
  workflow_id: id,
  tool_name: StoredMcpProfileToolNameSchema,
  tool_description: description.nullable(),
  position: z.number().int().min(0).max(MCP_PROFILE_MAX_TOOLS - 1)
}).strict()

export type McpProfileInput = z.infer<typeof McpProfileInputSchema>
export type McpProfileRow = z.infer<typeof McpProfileRowSchema>
export type McpProfileSummaryRow = z.infer<typeof McpProfileSummaryRowSchema>
export type McpProfileToolRow = z.infer<typeof McpProfileToolRowSchema>
export type McpProfile = McpProfileRow & { tools: McpProfileToolRow[] }
